import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPDataGrid } from 'components/data-grid';
import { Box, Checkbox, InputLabel } from '@mui/material';
import UPButton from 'components/button';
import { useAppSelector } from 'store';
import { toNumber } from 'lodash';
import { companiesWithExceptionsSelector } from 'store/selectors/wage-indexation-selectors';
import {
  COMPANY_PAYROLL__WAGE_EXEMPTION,
  MENU__COMPANY,
  WAGE_INDEXATION__COMPANY_WITH_EXCEPTIONS,
  WAGE_INDEXATION__CURRENT_WAGE,
  WAGE_INDEXATION__NEW_WAGE,
  WAGE_INDEXATION__NEXT_STEP,
  WAGE_INDEXATION__PREVIOUS_STEP,
  WAGE_INDEXATION__SHOULD_INDEX,
} from 'translations/constants';
import { GridColDef } from '@mui/x-data-grid';
import {
  WageIndexationFlowButtonContainer,
  WageIndexationFlowCard,
  WageIndexationFlowStepContainer,
  WageIndexationGridContainer,
} from './styles';
import { WageIndexationFlowContext } from './WageIndexation';
import { CompanyExceptionModel } from './types';

const CompaniesWithExceptionsGrid: React.FC = () => {
  const { onClickNextStep, onClickPreviousStep, activeStep, setCompanyExceptions } =
    useContext(WageIndexationFlowContext);
  const [t] = useTranslation();

  const [rowOnHover, setRowOnHover] = useState<number>();

  const exceptions = useAppSelector(companiesWithExceptionsSelector);

  const [updatedExceptions, setUpdatedExceptions] = useState<CompanyExceptionModel[]>([]);

  useEffect(() => {
    if (exceptions) setUpdatedExceptions(exceptions);
  }, [exceptions]);

  const onSaveExceptionValues = () => {
    // save to context the current values
    setCompanyExceptions(updatedExceptions);
    onClickNextStep();
  };

  const addOrUpdateEntity = useCallback(
    (entity: CompanyExceptionModel, field: string, value: any): CompanyExceptionModel => {
      const updatedEntity: CompanyExceptionModel = {
        ...entity,
        [field]: value,
      };

      const swap = updatedExceptions.map((item: CompanyExceptionModel) => {
        if (item.wageExceptionId === updatedEntity.wageExceptionId) {
          return updatedEntity;
        }
        return item;
      });

      setUpdatedExceptions(swap);

      return updatedEntity;
    },
    [updatedExceptions],
  );

  const handleSelectAll = event => {
    const newRows = updatedExceptions.map(row => ({
      ...row,
      shouldIndex: event.target.checked,
    }));

    setUpdatedExceptions(newRows);
  };

  const checkAllFromCompany = useCallback(
    (entity: CompanyExceptionModel, fieldName: string, checked: boolean) => {
      const swap = updatedExceptions.map((item: CompanyExceptionModel) => {
        if (item.companyId === entity.companyId) {
          const updatedEntity: CompanyExceptionModel = {
            ...item,
            [fieldName]: checked,
          };

          return updatedEntity;
        }
        return item;
      });

      setUpdatedExceptions(swap);
    },
    [updatedExceptions],
  );
  const columns: GridColDef[] = [
    { field: 'wageExceptionId', headerName: '', hide: true },
    { field: 'jointCommissionId', headerName: '', hide: true },
    { field: 'companyId', headerName: '', hide: true },
    { field: 'workerClass', headerName: '', hide: true },
    { field: 'functionId', headerName: '', hide: true },
    { field: 'employeeId', headerName: '', hide: true },
    { field: 'companyName', headerName: t(MENU__COMPANY), width: 300 },
    { field: 'exceptionType', headerName: t(COMPANY_PAYROLL__WAGE_EXEMPTION), width: 300 },
    { field: 'currentWage', headerName: t(WAGE_INDEXATION__CURRENT_WAGE), width: 200 },
    {
      field: 'newWage',
      headerName: t(WAGE_INDEXATION__NEW_WAGE),
      width: 200,
      editable: true,
      valueSetter: params => {
        if (Number.isNaN(toNumber(params.value)) || !params.value) {
          return params.row;
        }
        return addOrUpdateEntity(params.row as CompanyExceptionModel, 'newWage', toNumber(params.value));
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = Number.isNaN(toNumber(params.props.value));
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'shouldIndex',
      headerName: t(WAGE_INDEXATION__SHOULD_INDEX),
      width: 200,
      renderHeader: () => (
        <>
          <InputLabel>{t(WAGE_INDEXATION__SHOULD_INDEX)}</InputLabel>
          <Checkbox
            checked={updatedExceptions.length > 0 && updatedExceptions.every(r => r.shouldIndex)}
            indeterminate={!updatedExceptions.every(r => r.shouldIndex) && updatedExceptions.some(r => r.shouldIndex)}
            onChange={handleSelectAll}
          />
        </>
      ),
      renderCell: params => (
        <Checkbox
          checked={params.row.shouldIndex}
          onChange={e => {
            checkAllFromCompany(params.row as CompanyExceptionModel, 'shouldIndex', e.target.checked);
          }}
        />
      ),
    },
  ];

  return (
    <WageIndexationFlowStepContainer activeStep={activeStep}>
      <WageIndexationFlowCard>
        <h5 style={{ marginTop: 0, marginBottom: 0 }}>{t(WAGE_INDEXATION__COMPANY_WITH_EXCEPTIONS)}</h5>
        <WageIndexationGridContainer>
          <UPDataGrid
            rows={updatedExceptions}
            columns={columns}
            setRowOnHover={setRowOnHover}
            withCheckboxes={false}
            getRowId={r => r.wageExceptionId}
          />
        </WageIndexationGridContainer>
        <WageIndexationFlowButtonContainer>
          <UPButton text={t(WAGE_INDEXATION__PREVIOUS_STEP)} onClick={onClickPreviousStep} outlined />
          <UPButton text={t(WAGE_INDEXATION__NEXT_STEP)} onClick={onSaveExceptionValues} />
        </WageIndexationFlowButtonContainer>
      </WageIndexationFlowCard>
    </WageIndexationFlowStepContainer>
  );
};

export default CompaniesWithExceptionsGrid;
